import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  "aria-label": "Switch profile",
  class: "profile-switcher min-w-0 bg-[#eff3f9] p-3 text-left"
}
const _hoisted_2 = ["aria-expanded", "disabled"]
const _hoisted_3 = {
  "aria-hidden": "true",
  class: "flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-blue-500 bg-blue-50 text-lg font-semibold text-blue-700 ring-2 ring-white ring-offset-2 ring-offset-blue-100"
}
const _hoisted_4 = { class: "min-w-0 flex-1" }
const _hoisted_5 = { class: "block break-words text-base font-medium leading-6 text-gray-900" }
const _hoisted_6 = {
  key: 0,
  class: "flex h-9 w-6 shrink-0 items-center justify-center rounded-full bg-[#e9eef6] text-gray-600"
}
const _hoisted_7 = { class: "sr-only" }
const _hoisted_8 = ["aria-busy"]
const _hoisted_9 = ["disabled", "aria-label", "onClick"]
const _hoisted_10 = { class: "min-w-0 flex-1" }
const _hoisted_11 = { class: "block break-words text-sm font-medium leading-5 text-gray-900" }
const _hoisted_12 = {
  key: 0,
  "aria-hidden": "true",
  class: "fas fa-spinner fa-spin shrink-0 text-xs text-blue-600 motion-reduce:animate-none"
}
const _hoisted_13 = {
  key: 0,
  role: "status",
  class: "px-3 pt-2 text-xs text-blue-600"
}
const _hoisted_14 = {
  key: 1,
  role: "alert",
  class: "mt-2 rounded-lg bg-red-50 p-2 text-xs leading-5 text-red-600"
}

import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import http from "@/services/http";

interface ClientAccount {
  id: number;
  company_name: string;
  current: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ClientProfileSwitcher',
  props: {
    companyName: {}
  },
  setup(__props: any) {

const props = __props;
const expanded = ref(true);
const currentAccount = computed(() => accounts.value.find(account => account.current));
const otherAccounts = computed(() => accounts.value.filter(account => !account.current));
const currentName = computed(() => currentAccount.value?.company_name || props.companyName);
const avatarColors = ["bg-orange-700", "bg-teal-700", "bg-violet-600", "bg-rose-700"];
const router = useRouter();
const accounts = ref<ClientAccount[]>([]);
const switchingId = ref<number | null>(null);
const error = ref("");

function initials(name: string) {
  return name.trim().split(/\s+/).slice(0, 2).map(word => word[0]).join("").toUpperCase() || "?";
}

async function loadAccounts() {
  error.value = "";
  try {
    const { data } = await http.get<{ accounts: ClientAccount[] }>("/api/auth/client-profiles");
    accounts.value = data.accounts;
  } catch {
    error.value = "Unable to load your profiles.";
  }
}

async function switchProfile(account: ClientAccount) {
  if (account.current || switchingId.value !== null) return;
  switchingId.value = account.id;
  error.value = "";
  try {
    await http.post("/api/auth/client-profiles/switch", { client_id: account.id });
    try {
      localStorage.setItem("dmsuite:client-profile-switched", String(Date.now()));
    } catch {
      // The current tab can still switch when browser storage is unavailable.
    }
    // Recreate stores, query caches, sockets and embedded apps for the new identity.
    window.location.replace(router.resolve("/").href);
  } catch {
    error.value = "Unable to switch profiles. Try again or sign in to refresh your accounts.";
    switchingId.value = null;
  }
}

onMounted(loadAccounts);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("button", {
      type: "button",
      class: _normalizeClass(["flex w-full items-center gap-3 rounded-t-[28px] bg-white px-3 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500", { 'rounded-b-[28px]': !expanded.value || !otherAccounts.value.length }]),
      "aria-expanded": otherAccounts.value.length ? expanded.value : undefined,
      disabled: !otherAccounts.value.length || switchingId.value !== null,
      "aria-controls": "client-profile-options",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (expanded.value = !expanded.value))
    }, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(initials(currentName.value)), 1),
      _createElementVNode("span", _hoisted_4, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(currentName.value), 1)
      ]),
      (otherAccounts.value.length)
        ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
            _createElementVNode("i", {
              "aria-hidden": "true",
              class: _normalizeClass(["fas text-xs", expanded.value ? 'fa-chevron-up' : 'fa-chevron-down'])
            }, null, 2),
            _createElementVNode("span", _hoisted_7, _toDisplayString(expanded.value ? 'Hide profiles' : 'Show profiles'), 1)
          ]))
        : _createCommentVNode("", true)
    ], 10, _hoisted_2),
    _withDirectives(_createElementVNode("div", {
      id: "client-profile-options",
      class: "mt-0.5 max-h-60 space-y-0.5 overflow-y-auto rounded-b-xl",
      "aria-busy": switchingId.value !== null
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otherAccounts.value, (account, index) => {
        return (_openBlock(), _createElementBlock("button", {
          key: account.id,
          type: "button",
          disabled: switchingId.value !== null,
          "aria-label": `Switch to ${account.company_name}, profile ${account.id}`,
          class: "flex w-full items-center gap-3 rounded-sm bg-white px-4 py-3 text-left transition-colors hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 disabled:cursor-wait disabled:opacity-60",
          onClick: ($event: any) => (switchProfile(account))
        }, [
          _createElementVNode("span", {
            "aria-hidden": "true",
            class: _normalizeClass(["flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium text-white", avatarColors[index % avatarColors.length]])
          }, _toDisplayString(initials(account.company_name)), 3),
          _createElementVNode("span", _hoisted_10, [
            _createElementVNode("span", _hoisted_11, _toDisplayString(account.company_name), 1)
          ]),
          (switchingId.value === account.id)
            ? (_openBlock(), _createElementBlock("i", _hoisted_12))
            : _createCommentVNode("", true)
        ], 8, _hoisted_9))
      }), 128))
    ], 8, _hoisted_8), [
      [_vShow, expanded.value && otherAccounts.value.length]
    ]),
    (switchingId.value !== null)
      ? (_openBlock(), _createElementBlock("p", _hoisted_13, " Opening your profile... "))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    (error.value && !accounts.value.length)
      ? (_openBlock(), _createElementBlock("button", {
          key: 2,
          type: "button",
          class: "mt-2 rounded px-2 py-1 text-xs font-semibold text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
          onClick: loadAccounts
        }, " Retry "))
      : _createCommentVNode("", true)
  ]))
}
}

})